import { ApolloProvider } from '@apollo/client';
import type { Country } from '@seek/audience-zones';
import { MelwaysProvider } from '@seek/melways-react';
import { metrics } from '@seek/metrics-js';
import { VocabProvider } from '@vocab/react';
import { BraidProvider, ToastProvider } from 'braid-design-system';
import seekJobs from 'braid-design-system/themes/seekJobs';
import { hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { ScrollProvider } from 'src/components/Router/ScrollContext';
import { MOCK_API_URL, SEEK_ENV } from 'src/config';
import { FormProvider } from 'src/context/FormContext';
import { analyticsClient } from 'src/modules/analytics';
import { apiClient } from 'src/modules/api-client';

// @ts-expect-error
import createStore from './createStore';
import { App } from './handlers/App';
import { getCurrentPage } from './middlewares/utils';
import { AnalyticsProvider } from './modules/analytics/AnalyticsProvider';
import type { AnalyticsBrand } from './modules/analytics/types';
import { AppConfigProvider } from './modules/appConfig/AppConfigProvider';
import { RetryActionProvider } from './modules/retry-actions/RetryActionProvider';
import { getQueryParams } from './modules/utils';
import type { ClientContext } from './render';
import { siteNameToCountryMap } from './siteNameMaps';

export default ({
  site,
  language,
  vocabLanguage,
  locale,
  brand,
  zone,
}: ClientContext) => {
  analyticsClient.updateAnalyticsState({
    siteCountry: siteNameToCountryMap[site].toLowerCase() as Lowercase<Country>,
    brand: brand.toLowerCase() as AnalyticsBrand,
    zone,
    siteLanguage: language,
  });

  const client = apiClient({
    seekEnv: SEEK_ENV,
    mockApi: MOCK_API_URL,
    language,
    zone,
    site,
  });

  const appConfigParam = {
    site,
    zone,
    brand,
    locale,
    language,
  };

  const page = getCurrentPage();

  const store = createStore(analyticsClient, undefined, client, appConfigParam);

  const { pseudoLocalization = '' } = getQueryParams(window.location.href);

  metrics.addGlobalTags([`page-name:${page}`]);
  metrics.addGlobalTags([`country:${locale}`, `language:${language}`]);

  const container = document.getElementById('app');

  hydrateRoot(
    container!,
    <Provider store={store}>
      <AnalyticsProvider value={analyticsClient}>
        <RetryActionProvider value={{}}>
          <ApolloProvider client={client.apolloClient}>
            <VocabProvider
              language={
                pseudoLocalization && SEEK_ENV !== 'prod'
                  ? 'en-PSEUDO'
                  : vocabLanguage
              }
              {...(vocabLanguage === 'th-TH'
                ? { locale: 'th-u-ca-gregory' }
                : {})}
            >
              <MelwaysProvider.Client site={site}>
                <BraidProvider theme={seekJobs}>
                  <AppConfigProvider param={appConfigParam}>
                    <ToastProvider>
                      <ScrollProvider>
                        <BrowserRouter>
                          <FormProvider>
                            <App />
                          </FormProvider>
                        </BrowserRouter>
                      </ScrollProvider>
                    </ToastProvider>
                  </AppConfigProvider>
                </BraidProvider>
              </MelwaysProvider.Client>
            </VocabProvider>
          </ApolloProvider>
        </RetryActionProvider>
      </AnalyticsProvider>
    </Provider>,
  );
};
